import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CreditCardIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ClockIcon,
  BuildingOfficeIcon,
  BoltIcon,
  SignalIcon,
  ShieldCheckIcon,
  TagIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { SearchableUserSelect } from '@/components/ui/SearchableUserSelect'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { institutionsApi } from '@/api/institutionsApi'
import { getUsers } from '@/api/usersApi'
import { 
  getRFIDCards, 
  createRFIDCard, 
  updateRFIDCard, 
  deleteRFIDCard, 
  assignRFIDCard,
  unassignRFIDCard,
  getRFIDCardStats,
  type RFIDCard,
  type CreateRFIDCardRequest,
  type RFIDCardStats,
  type AssignRFIDCardRequest
} from '@/api/rfidApi'
import toast from 'react-hot-toast'

// Form schema for adding new RFID card
const addCardSchema = z.object({
  cardNumber: z.string().min(3, 'Card number must be at least 3 characters'),
  rfidTag: z.string().min(3, 'RFID tag must be at least 3 characters'),
  assignedToId: z.string().optional(),
  assignedToType: z.enum(['student', 'teacher', 'parent', 'driver', 'admin', 'visitor']),
  cardType: z.enum(['wristband', 'card', 'keychain', 'sticker']),
  expiryDate: z.string().optional(),
  notes: z.string().optional(),
  institutionId: z.string().optional(),
})

// Form schema for assigning card to user
const assignCardSchema = z.object({
  assignedToId: z.string().min(1, 'User is required'),
  assignedToType: z.enum(['student', 'teacher', 'parent', 'driver', 'admin', 'visitor']),
})

type AddCardData = z.infer<typeof addCardSchema>
type AssignCardData = z.infer<typeof assignCardSchema>

const RFIDCardsPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [cards, setCards] = useState<RFIDCard[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [assignedFilter, setAssignedFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedCard, setSelectedCard] = useState<RFIDCard | null>(null)
  const [cardToAssign, setCardToAssign] = useState<RFIDCard | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  
  // Available data for forms
  const [availableInstitutions, setAvailableInstitutions] = useState<{id: string, name: string}[]>([])
  
  // Stats state
  const [stats, setStats] = useState<RFIDCardStats>({
    totalCards: 0,
    activeCards: 0,
    inactiveCards: 0,
    assignedCards: 0,
    unassignedCards: 0,
    lostCards: 0,
    damagedCards: 0,
    suspendedCards: 0,
    byType: {},
    byStatus: {},
    byAssignedType: {},
    byInstitution: {},
    totalScansToday: 0
  })

  const addCardForm = useForm<AddCardData>({
    resolver: zodResolver(addCardSchema),
    defaultValues: {
      cardNumber: '',
      rfidTag: '',
      assignedToId: '',
      assignedToType: 'student',
      cardType: 'wristband',
      expiryDate: '',
      notes: '',
      institutionId: '',
    }
  })

  const assignCardForm = useForm<AssignCardData>({
    resolver: zodResolver(assignCardSchema),
    defaultValues: {
      assignedToId: '',
      assignedToType: 'student',
    }
  })

  useEffect(() => {
    fetchCards()
    if (userRole === 'super_admin') {
      fetchInstitutions()
    }
  }, [])

  useEffect(() => {
    // Re-fetch cards when filters change
    const timeoutId = setTimeout(() => {
      fetchCards()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter, typeFilter, assignedFilter, institutionFilter])

  const fetchCards = async () => {
    setLoading(true)
    try {
      console.log('Fetching RFID cards...')
      
      const params: any = {}
      
      if (searchQuery.trim()) params.search = searchQuery.trim()
      if (statusFilter !== 'all') params.status = statusFilter
      if (typeFilter !== 'all') params.cardType = typeFilter
      if (assignedFilter !== 'all') params.assigned = assignedFilter === 'true'
      if (institutionFilter !== 'all' && userRole === 'super_admin') {
        params.institutionId = institutionFilter
      }
      
      const [cardsResponse, statsResponse] = await Promise.all([
        getRFIDCards(params),
        getRFIDCardStats()
      ])
      
      console.log('RFID cards response:', cardsResponse)
      console.log('Stats response:', statsResponse)
      
      setCards(cardsResponse.data || [])
      setStats(statsResponse.data || {
        totalCards: 0,
        activeCards: 0,
        inactiveCards: 0,
        assignedCards: 0,
        unassignedCards: 0,
        lostCards: 0,
        damagedCards: 0,
        suspendedCards: 0,
        byType: {},
        byStatus: {},
        byAssignedType: {},
        byInstitution: {},
        totalScansToday: 0
      })
      
    } catch (error: any) {
      console.error('Failed to fetch RFID cards:', error)
      toast.error(error.response?.data?.message || 'Failed to load RFID cards')
      setCards([])
    } finally {
      setLoading(false)
    }
  }

  const fetchInstitutions = async () => {
    try {
      console.log('Fetching institutions...')
      const institutions = await institutionsApi.getInstitutions()
      
      const institutionsList = institutions.map(institution => ({
        id: institution.id,
        name: institution.name
      }))
      
      setAvailableInstitutions(institutionsList)
    } catch (error: any) {
      console.error('Failed to fetch institutions:', error)
      toast.error('Failed to load institutions')
      setAvailableInstitutions([])
    }
  }

  // fetchUsers function removed - now handled by SearchableUserSelect component

  const handleAddCard = async (cardData: AddCardData) => {
    try {
      console.log('Creating RFID card:', cardData)
      
      const createData: CreateRFIDCardRequest = {
        cardNumber: cardData.cardNumber,
        rfidTag: cardData.rfidTag,
        assignedToId: cardData.assignedToId || undefined,
        assignedToType: cardData.assignedToType,
        cardType: cardData.cardType,
        expiryDate: cardData.expiryDate || undefined,
        notes: cardData.notes || undefined,
        institutionId: cardData.institutionId || undefined
      }
      
      const response = await createRFIDCard(createData)
      console.log('RFID card created:', response)
      
      toast.success(`RFID card "${response.data.cardNumber}" created successfully!`)
      setShowAddModal(false)
      addCardForm.reset()
      fetchCards() // Refresh the cards list
      
    } catch (error: any) {
      console.error('Failed to create RFID card:', error)
      toast.error(error.response?.data?.message || 'Failed to create RFID card')
    }
  }

  const handleAssignCard = async (assignData: AssignCardData) => {
    if (!cardToAssign) return
    
    try {
      console.log('Assigning RFID card:', assignData)
      
      const response = await assignRFIDCard(cardToAssign.id, {
        assignedToId: assignData.assignedToId,
        assignedToType: assignData.assignedToType
      })
      
      toast.success(`Card "${cardToAssign.cardNumber}" assigned successfully!`)
      setShowAssignModal(false)
      setCardToAssign(null)
      assignCardForm.reset()
      fetchCards() // Refresh the cards list
      
    } catch (error: any) {
      console.error('Failed to assign RFID card:', error)
      toast.error(error.response?.data?.message || 'Failed to assign RFID card')
    }
  }

  const handleUnassignCard = async (card: RFIDCard) => {
    if (confirm(`Are you sure you want to unassign card "${card.cardNumber}" from ${card.assignedToName}?`)) {
      try {
        await unassignRFIDCard(card.id)
        toast.success(`Card "${card.cardNumber}" unassigned successfully`)
        fetchCards()
      } catch (error: any) {
        console.error('Failed to unassign card:', error)
        toast.error(error.response?.data?.message || 'Failed to unassign card')
      }
    }
  }

  const handleViewCard = (card: RFIDCard) => {
    setSelectedCard(card)
  }

  const handleEditCard = (card: RFIDCard) => {
    toast.success(`Edit "${card.cardNumber}" - Coming soon!`)
  }

  const handleDeleteCard = async (card: RFIDCard) => {
    if (confirm(`Are you sure you want to delete card "${card.cardNumber}"?`)) {
      try {
        await deleteRFIDCard(card.id)
        toast.success(`Card "${card.cardNumber}" deleted successfully`)
        fetchCards()
      } catch (error: any) {
        console.error('Failed to delete card:', error)
        toast.error(error.response?.data?.message || 'Failed to delete card')
      }
    }
  }

  const openAssignModal = (card: RFIDCard) => {
    setCardToAssign(card)
    setShowAssignModal(true)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'lost':
        return 'bg-red-100 text-red-800'
      case 'damaged':
        return 'bg-orange-100 text-orange-800'
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4 text-green-600" />
      case 'inactive':
        return <XCircleIcon className="h-4 w-4 text-gray-600" />
      case 'lost':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
      case 'damaged':
        return <ExclamationTriangleIcon className="h-4 w-4 text-orange-600" />
      case 'suspended':
        return <ShieldCheckIcon className="h-4 w-4 text-yellow-600" />
      default:
        return <CreditCardIcon className="h-4 w-4 text-gray-600" />
    }
  }

  const getTypeColor = (cardType: string) => {
    switch (cardType) {
      case 'wristband':
        return 'bg-blue-100 text-blue-800'
      case 'card':
        return 'bg-purple-100 text-purple-800'
      case 'keychain':
        return 'bg-green-100 text-green-800'
      case 'sticker':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getAssignedTypeColor = (assignedType: string) => {
    switch (assignedType) {
      case 'student':
        return 'bg-blue-100 text-blue-800'
      case 'teacher':
        return 'bg-green-100 text-green-800'
      case 'parent':
        return 'bg-purple-100 text-purple-800'
      case 'driver':
        return 'bg-orange-100 text-orange-800'
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'visitor':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatLastUsed = (lastUsed?: string) => {
    if (!lastUsed) return 'Never used'
    
    const diff = Date.now() - new Date(lastUsed).getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return new Date(lastUsed).toLocaleDateString()
  }

  const isCardExpired = (expiryDate?: string) => {
    if (!expiryDate) return false
    return new Date(expiryDate) < new Date()
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CreditCardIcon className="h-8 w-8 text-blue-600 mr-3" />
            RFID Cards & Wristbands
          </h1>
          <p className="text-gray-600 mt-1">
            Manage RFID cards and smart wristbands for {userRole === 'super_admin' ? 'all institutions' : 'your institution'}
          </p>
        </div>

        <div className="flex space-x-3">
          {/* {userRole === 'super_admin' && ( */}
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Card
          </button>
          {/* )} */}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCards}</p>
            </div>
            <CreditCardIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeCards}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Assigned</p>
              <p className="text-2xl font-bold text-blue-600">{stats.assignedCards}</p>
            </div>
            <UserIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unassigned</p>
              <p className="text-2xl font-bold text-gray-600">{stats.unassignedCards}</p>
            </div>
            <TagIcon className="h-8 w-8 text-gray-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Lost</p>
              <p className="text-2xl font-bold text-red-600">{stats.lostCards}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Damaged</p>
              <p className="text-2xl font-bold text-orange-600">{stats.damagedCards}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.suspendedCards}</p>
            </div>
            <ShieldCheckIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Scans Today</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalScansToday.toLocaleString()}</p>
            </div>
            <SignalIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col xl:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by card number, RFID tag, or user..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="lost">Lost</option>
            <option value="damaged">Damaged</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Types</option>
            <option value="wristband">Wristband</option>
            <option value="card">Card</option>
            <option value="keychain">Keychain</option>
            <option value="sticker">Sticker</option>
          </select>

          <select
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Cards</option>
            <option value="true">Assigned</option>
            <option value="false">Unassigned</option>
          </select>

          {userRole === 'super_admin' && (
            <select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              className="form-input text-sm"
            >
              <option value="all">All Institutions</option>
              {availableInstitutions.map(institution => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          )}

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Cards Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewCard(card)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <CreditCardIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{card.cardNumber}</h3>
                    <p className="text-sm text-gray-600 font-mono">{card.rfidTag}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(card.status)}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(card.status)}`}>
                    {card.status}
                  </span>
                </div>
              </div>

              {/* Assignment Info */}
              <div className="space-y-2 mb-4">
                {card.assignedToName ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Assigned to:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAssignedTypeColor(card.assignedToType)}`}>
                        {card.assignedToType}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mt-1">{card.assignedToName}</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="text-center">
                      <TagIcon className="h-6 w-6 text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500 mt-1">Unassigned</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Type:</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(card.cardType)}`}>
                    {card.cardType}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Scans today:</span>
                  <span className="text-gray-900 font-medium">{card.dailyScans}</span>
                </div>

                {card.batteryLevel && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Battery:</span>
                    <span className={`font-medium ${card.batteryLevel > 20 ? 'text-green-600' : 'text-red-600'}`}>
                      {card.batteryLevel}%
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  Last used: {formatLastUsed(card.lastUsed)}
                </span>
                <div className="flex space-x-1">
                  {!card.assignedToName ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openAssignModal(card)
                      }}
                      className="btn btn-ghost text-xs text-green-600"
                      title="Assign Card"
                    >
                      <UserPlusIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUnassignCard(card)
                      }}
                      className="btn btn-ghost text-xs text-orange-600"
                      title="Unassign Card"
                    >
                      <UserMinusIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditCard(card)
                    }}
                    className="btn btn-ghost text-xs"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteCard(card)
                    }}
                    className="btn btn-ghost text-xs text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Expiry warning */}
              {card.expiryDate && isCardExpired(card.expiryDate) && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                    <span className="text-xs text-red-700">Expired</span>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Cards Table View */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Card Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type & Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  {userRole === 'super_admin' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institution
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cards.map((card) => (
                  <motion.tr
                    key={card.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewCard(card)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                          <CreditCardIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{card.cardNumber}</div>
                          <div className="text-sm text-gray-500 font-mono">{card.rfidTag}</div>
                          {card.batteryLevel && (
                            <div className="flex items-center space-x-1 mt-1">
                              <BoltIcon className={`h-3 w-3 ${card.batteryLevel > 20 ? 'text-green-600' : 'text-red-600'}`} />
                              <span className={`text-xs ${card.batteryLevel > 20 ? 'text-green-600' : 'text-red-600'}`}>
                                {card.batteryLevel}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {card.assignedToName ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{card.assignedToName}</div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAssignedTypeColor(card.assignedToType)}`}>
                            {card.assignedToType}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <TagIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Unassigned</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(card.cardType)}`}>
                          {card.cardType}
                        </span>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(card.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(card.status)}`}>
                            {card.status}
                          </span>
                        </div>
                        {card.expiryDate && isCardExpired(card.expiryDate) && (
                          <div className="flex items-center space-x-1">
                            <ExclamationTriangleIcon className="h-3 w-3 text-red-600" />
                            <span className="text-xs text-red-600">Expired</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{card.dailyScans} today</div>
                      <div className="text-xs text-gray-500">{card.totalScans.toLocaleString()} total</div>
                      <div className="text-xs text-gray-500">{formatLastUsed(card.lastUsed)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(card.issueDate).toLocaleDateString()}
                      </div>
                      {card.expiryDate && (
                        <div className={`text-xs ${isCardExpired(card.expiryDate) ? 'text-red-600' : 'text-gray-500'}`}>
                          Exp: {new Date(card.expiryDate).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    {userRole === 'super_admin' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{card.institutionName}</div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        {!card.assignedToName ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openAssignModal(card)
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Assign Card"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUnassignCard(card)
                            }}
                            className="text-orange-600 hover:text-orange-900"
                            title="Unassign Card"
                          >
                            <UserMinusIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditCard(card)
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteCard(card)
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {cards.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No RFID cards found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first RFID card'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add First Card
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Card Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add RFID Card</h3>
                    <p className="text-sm text-gray-500">Register a new RFID card or wristband</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addCardForm.handleSubmit(handleAddCard)} className="p-6 space-y-6">
                
                {/* Card Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCardIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Card Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addCardForm.register('cardNumber')}
                      label="Card Number"
                      placeholder="STS-001-2025"
                      error={addCardForm.formState.errors.cardNumber?.message}
                    />

                    <Input
                      {...addCardForm.register('rfidTag')}
                      label="RFID Tag"
                      placeholder="RF123456789ABC"
                      error={addCardForm.formState.errors.rfidTag?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Type
                      </label>
                      <select
                        {...addCardForm.register('cardType')}
                        className="form-input"
                      >
                        <option value="wristband">Smart Wristband</option>
                        <option value="card">RFID Card</option>
                        <option value="keychain">Keychain</option>
                        <option value="sticker">RFID Sticker</option>
                      </select>
                    </div>

                    <Input
                      {...addCardForm.register('expiryDate')}
                      type="date"
                      label="Expiry Date (Optional)"
                      error={addCardForm.formState.errors.expiryDate?.message}
                    />
                  </div>
                </div>

                {/* Assignment */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-green-600 mr-2" />
                    Assignment (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SearchableUserSelect
                      label="Assign to User (Optional)"
                      placeholder="Search and select user to assign card..."
                      value={addCardForm.watch('assignedToId')}
                      onChange={(userId) => {
                        addCardForm.setValue('assignedToId', userId || '')
                      }}
                      institutionId={userRole === 'super_admin' ? addCardForm.watch('institutionId') : undefined}
                      error={addCardForm.formState.errors.assignedToId?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        User Type
                      </label>
                      <select
                        {...addCardForm.register('assignedToType')}
                        className="form-input"
                      >
                        <option value="student">Student</option>
                        <option value="teacher">Teacher</option>
                        <option value="parent">Parent</option>
                        <option value="driver">Driver</option>
                        <option value="admin">Admin</option>
                        <option value="visitor">Visitor</option>
                      </select>
                    </div>

                    {userRole === 'super_admin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Institution
                        </label>
                        <select
                          {...addCardForm.register('institutionId')}
                          className="form-input"
                        >
                          <option value="">Select Institution</option>
                          {availableInstitutions.map(institution => (
                            <option key={institution.id} value={institution.id}>
                              {institution.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <Input
                        {...addCardForm.register('notes')}
                        label="Notes (Optional)"
                        placeholder="Additional information about this card"
                        error={addCardForm.formState.errors.notes?.message}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">RFID Card Registration</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Card will be registered and ready for assignment</p>
                        <p>• If assigned during creation, card will be automatically activated</p>
                        <p>• Unassigned cards remain inactive until assigned to a user</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CreditCardIcon className="h-4 w-4 mr-2" />
                    Register Card
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Card Modal */}
      <AnimatePresence>
        {showAssignModal && cardToAssign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAssignModal(false)
              setCardToAssign(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Assign RFID Card</h3>
                    <p className="text-sm text-gray-500">Assign card {cardToAssign.cardNumber} to a user</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAssignModal(false)
                      setCardToAssign(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={assignCardForm.handleSubmit(handleAssignCard)} className="p-6 space-y-4">
                <SearchableUserSelect
                  label="Select User"
                  placeholder="Search for user to assign card..."
                  value={assignCardForm.watch('assignedToId')}
                  onChange={(userId, user) => {
                    assignCardForm.setValue('assignedToId', userId || '')
                    if (user) {
                      // Auto-detect user type based on role
                      const userType = user.role.toLowerCase().includes('student') ? 'student' :
                                      user.role.toLowerCase().includes('teacher') ? 'teacher' :
                                      user.role.toLowerCase().includes('parent') ? 'parent' :
                                      user.role.toLowerCase().includes('driver') ? 'driver' :
                                      user.role.toLowerCase().includes('admin') ? 'admin' : 'visitor'
                      assignCardForm.setValue('assignedToType', userType as any)
                    }
                  }}
                  required={true}
                  error={assignCardForm.formState.errors.assignedToId?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Type
                  </label>
                  <select
                    {...assignCardForm.register('assignedToType')}
                    className="form-input"
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="parent">Parent</option>
                    <option value="driver">Driver</option>
                    <option value="admin">Admin</option>
                    <option value="visitor">Visitor</option>
                  </select>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <UserPlusIcon className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-green-900">Card Assignment</h5>
                      <div className="text-sm text-green-700 mt-1">
                        <p>Card will be activated and assigned to the selected user</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowAssignModal(false)
                      setCardToAssign(null)
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    Assign Card
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Details Modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <CreditCardIcon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedCard.cardNumber}</h3>
                      <p className="text-sm text-gray-500 font-mono">{selectedCard.rfidTag}</p>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(selectedCard.cardType)}`}>
                          {selectedCard.cardType}
                        </span>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(selectedCard.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedCard.status)}`}>
                            {selectedCard.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCard(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Card Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Card Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Card Number</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedCard.cardNumber}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">RFID Tag</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedCard.rfidTag}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Type</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(selectedCard.cardType)}`}>
                          {selectedCard.cardType}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(selectedCard.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedCard.status)}`}>
                            {selectedCard.status}
                          </span>
                        </div>
                      </div>
                      {selectedCard.batteryLevel && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Battery Level</span>
                          <div className="flex items-center space-x-2">
                            <BoltIcon className={`h-4 w-4 ${selectedCard.batteryLevel > 20 ? 'text-green-600' : 'text-red-600'}`} />
                            <span className={`text-sm font-medium ${selectedCard.batteryLevel > 20 ? 'text-green-600' : 'text-red-600'}`}>
                              {selectedCard.batteryLevel}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Assignment Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Assignment</h4>
                    {selectedCard.assignedToName ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="h-5 w-5 text-green-600" />
                          <div>
                            <div className="text-sm font-medium text-green-900">{selectedCard.assignedToName}</div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAssignedTypeColor(selectedCard.assignedToType)}`}>
                              {selectedCard.assignedToType}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <TagIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">Unassigned</div>
                            <div className="text-xs text-gray-500">Available for assignment</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Usage Statistics */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Usage Statistics</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Scans Today</span>
                        <span className="text-sm text-gray-900 font-bold">{selectedCard.dailyScans}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Scans</span>
                        <span className="text-sm text-gray-900 font-bold">{selectedCard.totalScans.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Last Used</span>
                        <span className="text-sm text-gray-900">{formatLastUsed(selectedCard.lastUsed)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Issue Date</span>
                        <span className="text-sm text-gray-900">{new Date(selectedCard.issueDate).toLocaleDateString()}</span>
                      </div>
                      {selectedCard.expiryDate && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Expiry Date</span>
                          <span className={`text-sm font-medium ${isCardExpired(selectedCard.expiryDate) ? 'text-red-600' : 'text-gray-900'}`}>
                            {new Date(selectedCard.expiryDate).toLocaleDateString()}
                            {isCardExpired(selectedCard.expiryDate) && ' (Expired)'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedCard.notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedCard.notes}</p>
                  </div>
                )}

                {/* System Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">System Information</h4>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Created by: </span>
                      <span className="text-gray-900 font-medium">{selectedCard.createdBy}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Institution: </span>
                      <span className="text-gray-900 font-medium">{selectedCard.institutionName}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Created: </span>
                      <span className="text-gray-900 font-medium">
                        {new Date(selectedCard.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Last Updated: </span>
                      <span className="text-gray-900 font-medium">
                        {new Date(selectedCard.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-between">
                  <div className="flex space-x-3">
                    {!selectedCard.assignedToName ? (
                      <Button
                        onClick={() => {
                          setSelectedCard(null)
                          openAssignModal(selectedCard)
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <UserPlusIcon className="h-4 w-4 mr-2" />
                        Assign Card
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedCard(null)
                          handleUnassignCard(selectedCard)
                        }}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <UserMinusIcon className="h-4 w-4 mr-2" />
                        Unassign Card
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => {
                        setSelectedCard(null)
                        handleEditCard(selectedCard)
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit Card
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RFIDCardsPage